import { Navigate, Route, Routes } from 'react-router-dom';
import { IonRouterOutlet, IonSpinner, useIonRouter } from "@ionic/react";
import "./App.css";
import { useAuth0 } from "@auth0/auth0-react";
import { Landing } from "./pages/Landing";
import { TabsPage } from "./pages/TabsPage";
import { AuthCallback } from "./pages/AuthCallback";
import { CompleteProfilePage } from "./pages/CompleteProfilePage";
import { ProfileGuard } from "./components/ProfileGuard";
import { AuthGuard } from "./components/AuthGuard";
import { CreateFantasyLeague } from "./pages/main-tabs/my-leagues/CreateFantasyLeaguePage";
import { FantasyLeagueTabsPage } from "./pages/fantasy-leagues-tabs/FantasyLeagueTabsPage";
import { LeagueSettingsPageRoute } from "./pages/fantasy-leagues-tabs/settings/LeagueSettingsPageRoute";
import { useEffect } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import { AnalyticsPageTracker } from "./components/AnalyticsPageTracker";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { AdminArea } from "./pages/admin/AdminArea";

// Wrap components with ProfileGuard first, then with authentication
const TabsPageWithProfileGuard = () => (
  <ProfileGuard>
    <TabsPage />
  </ProfileGuard>
);

const CreateFantasyLeagueWithProfileGuard = () => (
  <ProfileGuard>
    <CreateFantasyLeague />
  </ProfileGuard>
);

const FantasyLeagueTabsPageWithProfileGuard = () => (
  <ProfileGuard>
    <FantasyLeagueTabsPage />
  </ProfileGuard>
);

// Create the protected components outside of the render cycle
const ProtectedTabsPage = () => (
  <AuthGuard>
    <TabsPageWithProfileGuard />
  </AuthGuard>
);

const ProtectedCompleteProfilePage = () => (
  <AuthGuard>
    <CompleteProfilePage />
  </AuthGuard>
);

const ProtectedCreateFantasyLeague = () => (
  <AuthGuard>
    <CreateFantasyLeagueWithProfileGuard />
  </AuthGuard>
);

const ProtectedFantasyLeagueTabsPage = () => (
  <AuthGuard>
    <FantasyLeagueTabsPageWithProfileGuard />
  </AuthGuard>
);

// Standalone full-screen league settings page (rendered outside the tab bar)
const ProtectedFantasyLeagueSettingsPage = () => (
  <AuthGuard>
    <ProfileGuard>
      <LeagueSettingsPageRoute />
    </ProfileGuard>
  </AuthGuard>
);

const ProtectedAdminArea = () => (
  <AuthGuard>
    <ProfileGuard>
      <AdminArea />
    </ProfileGuard>
  </AuthGuard>
);

function App() {
  const { isLoading, isAuthenticated, handleRedirectCallback } = useAuth0();
  const isMobile = Capacitor.isNativePlatform();
  const ionRouter = useIonRouter();

  // Handle deep links for mobile - specifically for Auth0 callback and navigation
  useEffect(() => {
    // Only set up listener on mobile
    if (!isMobile) {
      return;
    }

    const handleAppUrlOpen = async (data: { url: string }) => {
      // Check if this is an Auth0 callback URL (login/logout callback)
      if (
        data.url.includes("state") &&
        (data.url.includes("code") || data.url.includes("error"))
      ) {
        try {
          await handleRedirectCallback(data.url);
          // Navigate to callback page to complete the flow
          ionRouter.push("/callback", "forward", "replace");
        } catch (error) {
          console.error("[Deep Link] Error handling redirect callback:", error);
        }
      } else {
        // Handle other deep links (e.g., logout redirect to landing)
        const url = new URL(data.url);
        const host = url.host || url.pathname.replace(/^\/+/, "");

        // Navigate to the appropriate route
        if (host === "landing" || host === "") {
          ionRouter.push("/landing", "back", "replace");
        }
      }

      // Close the browser (no-op on Android)
      await Browser.close();
    };

    // Set up listener and immediately store the promise
    const listenerPromise = CapacitorApp.addListener(
      "appUrlOpen",
      handleAppUrlOpen
    );

    return () => {
      // Remove the listener when component unmounts
      listenerPromise.then((listener) => {
        listener.remove();
      });
    };
  }, [isMobile, handleRedirectCallback, ionRouter]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <IonSpinner name="crescent" className="w-12 h-12" />
      </div>
    );
  }

  return (
    <>
      <AnalyticsPageTracker />
      <IonRouterOutlet>
        <Routes>
          <Route
            path="/"
            element={
              // For mobile: show landing if not authenticated, go to app if authenticated
              // For web: always go to app (auth guard will handle redirect)
              <Navigate to={!isAuthenticated && !isLoading ? '/landing' : '/app'} replace />
            }
          />
          <Route path="/landing" element={<Landing />} />
          <Route path="/callback" element={<AuthCallback />} />
          <Route
            path="/complete-profile"
            element={<ProtectedCompleteProfilePage />}
          />
          <Route
            path="/app/ligas/crear"
            element={<ProtectedCreateFantasyLeague />}
          />
          <Route
            path="/app/ligas/:leagueId/ajustes"
            element={<ProtectedFantasyLeagueSettingsPage />}
          />
          <Route
            path="/app/ligas/:leagueId/*"
            element={<ProtectedFantasyLeagueTabsPage />}
          />
          <Route path="/app/admin/*" element={<ProtectedAdminArea />} />
          <Route path="/app/main/*" element={<ProtectedTabsPage />} />
          <Route path="/app" element={<Navigate to="/app/main/mis-ligas" replace />} />
        </Routes>
      </IonRouterOutlet>
    </>
  );
}

export default App;
